import { useEffect, useMemo } from 'react';

import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import { UploadExplainerModal } from './components/modals/upload-explainer-modal';
import { Overview } from './overview';
import { RunLog } from './runs/run-log';
import { FileLog } from './uploads/file-log';
import { TrackingEventsV2, PageName } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { SettingsSectionHeading } from '@/new/components/settings-section';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

enum View {
  Overview = 'overview',
  FileLog = 'file-log',
  RunLog = 'run-log',
}

export function Uploads() {
  const { matterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  if (!matterId) return null;

  // Determine active tab from URL
  const activeTab = useMemo(() => {
    if (location.pathname.includes('file-log')) {
      return View.FileLog;
    } else if (location.pathname.includes('run-log')) {
      return View.RunLog;
    }
    return View.Overview;
  }, [location.pathname]);

  useEffect(() => {
    trackPageView(PageName.FileHistory);
  }, []);

  return (
    <>
      <SettingsSectionHeading
        title="File History"
        description="View a history of all of the files uploaded and processed in this Matter."
        inset={false}
        className="mb-6"
        action={<UploadExplainerModal />}
      />

      <div className="mb-4 flex items-center justify-between">
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            const tab = value as View;
            navigate(tab === View.Overview ? '' : tab);
            trackCustomEvent(TrackingEventsV2.FileHistoryTabChanged, { matterId, tab });
          }}
        >
          <TabsList variant="pill">
            <TabsTrigger value={View.Overview} className="text-sm">
              Overview
            </TabsTrigger>
            <TabsTrigger value={View.FileLog} className="text-sm">
              File History Log
            </TabsTrigger>
            <TabsTrigger value={View.RunLog} className="text-sm">
              Run History Log
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div>
        <Routes>
          <Route index element={<Overview />} />
          <Route path="file-log" element={<FileLog />} />
          <Route path="run-log" element={<RunLog />} />
        </Routes>
      </div>
    </>
  );
}
